'use client'

import { AlertOctagon, RefreshCw } from 'lucide-react'
import * as React from 'react'

import { extractErrorMessage } from '../error-message'
import { Button } from './button'

/**
 * Friendly top-level error fallback. Renders inside `ErrorBoundary` when
 * a descendant throws during render. Shows a generic "something went wrong"
 * panel, surfaces the error message + a "Try again" action that resets the
 * boundary, plus an optional "Reload page" for unrecoverable states. The
 * stack trace is tucked inside a collapsed `<details>` so end users see
 * only the message but devs (or curious users) can drill in.
 */
function DefaultErrorFallback({ error, reset }: { error: unknown; reset: () => void }) {
  const message = extractErrorMessage(error) ?? 'An unexpected error occurred.'
  const stack = error instanceof Error ? error.stack : null

  return (
    <div role="alert" aria-live="polite" className="flex min-h-[60vh] w-full items-center justify-center p-6">
      <div className="w-full max-w-md rounded-lg border border-border bg-card p-6 shadow-sm">
        <div className="mb-4 flex items-center gap-2 text-destructive">
          <AlertOctagon className="h-5 w-5" aria-hidden />
          <h1 className="text-base font-semibold">Something went wrong</h1>
        </div>
        <p className="mb-4 text-sm text-muted-foreground">{message}</p>
        <div className="flex flex-wrap gap-2">
          <Button onClick={reset} size="sm">
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" aria-hidden />
            Try again
          </Button>
          <Button variant="outline" size="sm" onClick={() => window.location.reload()}>
            Reload page
          </Button>
        </div>
        {stack && (
          <details className="mt-4 text-xs text-muted-foreground">
            <summary className="cursor-pointer select-none font-medium">Technical details</summary>
            <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap font-mono leading-relaxed">{stack}</pre>
          </details>
        )}
      </div>
    </div>
  )
}

export interface ErrorBoundaryProps {
  children: React.ReactNode
  /** Optional custom fallback. Receives the thrown error and a `reset()`
   *  callback that re-renders children. Defaults to a friendly panel. */
  fallback?: React.ComponentType<{ error: unknown; reset: () => void }>
  /** Called on every catch. Hook telemetry here (Faro, Sentry, etc.). */
  onError?: (error: unknown, info: React.ErrorInfo) => void
}

interface ErrorBoundaryState {
  error: unknown | null
}

/**
 * Generic React error boundary. Wrap your app's root with it (above the
 * router but below any provider that needs to stay mounted across errors)
 * so unhandled render-time errors surface a user-friendly panel instead
 * of a blank page or React's default debug overlay.
 *
 * `reset()` clears the captured error so a retry attempt can re-render.
 * Combine with `onError` to forward to telemetry; no telemetry is wired
 * into the primitive itself so this stays framework-agnostic.
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { error: null }

  static getDerivedStateFromError(error: unknown): ErrorBoundaryState {
    return { error }
  }

  componentDidCatch(error: unknown, info: React.ErrorInfo): void {
    this.props.onError?.(error, info)
    // Surface in the console too — production builds otherwise swallow it.

    console.error('[ErrorBoundary]', error, info.componentStack)
  }

  reset = (): void => {
    this.setState({ error: null })
  }

  render(): React.ReactNode {
    if (this.state.error !== null) {
      const Fallback = this.props.fallback ?? DefaultErrorFallback
      return <Fallback error={this.state.error} reset={this.reset} />
    }
    return this.props.children
  }
}
